import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, ChoiceCard, Container, Heading, Screen, Stack, Text } from '@fluent/ui';
import { useAuth, useAuthStore } from '../auth/authStore.js';
import { supabase } from '../lib/supabase.js';
import { playSfx } from '../lesson/sounds.js';
import './Onboarding.css';

/**
 * Onboarding — three-step intro for first-time users.
 *
 *   1. Welcome — brand pitch + "Let's go"
 *   2. Level — absolute beginner / some basics (informs lesson selection later)
 *   3. Daily goal — 5/10/15/20 min, persisted to profiles.daily_goal_minutes
 *
 * The flag `has_completed_onboarding` flips to true on submit; ProtectedRoute
 * routes existing users straight to /, new ones to /onboarding (see main.tsx).
 *
 * Level pick is currently informational — Phase 12 content will gate
 * "absolute beginner" vs "some basics" lesson sets. For now we just
 * persist it via user_metadata so the data is available when content lands.
 */

type Step = 0 | 1 | 2;
type Level = 'beginner' | 'basics';

const GOALS: ReadonlyArray<{ minutes: 5 | 10 | 15 | 20; titleKey: string; descKey: string }> = [
  { minutes: 5, titleKey: 'onboarding.goal.5_title', descKey: 'onboarding.goal.5_desc' },
  { minutes: 10, titleKey: 'onboarding.goal.10_title', descKey: 'onboarding.goal.10_desc' },
  { minutes: 15, titleKey: 'onboarding.goal.15_title', descKey: 'onboarding.goal.15_desc' },
  { minutes: 20, titleKey: 'onboarding.goal.20_title', descKey: 'onboarding.goal.20_desc' },
];

export function Onboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const setProfile = useAuthStore((s) => s.setProfile);

  const [step, setStep] = useState<Step>(0);
  const [level, setLevel] = useState<Level | null>(null);
  const [goal, setGoal] = useState<5 | 10 | 15 | 20 | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const next = (s: Step) => {
    playSfx('tap');
    setStep(s);
  };

  const finish = async () => {
    if (!user || !goal || !level) return;
    setSubmitting(true);
    setError(null);
    const { data, error: e } = await supabase
      .from('profiles')
      .update({
        daily_goal_minutes: goal,
        has_completed_onboarding: true,
      })
      .eq('id', user.id)
      .select()
      .single();
    if (e || !data) {
      setSubmitting(false);
      setError(t('onboarding.error_save'));
      return;
    }
    // Reflect locally so ProtectedRoute lets us through immediately on
    // navigate; the AuthProvider's onAuthStateChange listener will also
    // refresh on the next session event, but we don't want to wait.
    setProfile({ ...(profile ?? data), ...data });
    // Persist `level` separately on user metadata so the curriculum can
    // pick the starting lesson once Phase 12 differentiates. Best-effort:
    // nothing reads it yet, and a failure must not surface as an unhandled
    // rejection (Sentry JAVASCRIPT-REACT-F).
    supabase.auth.updateUser({ data: { level } }).catch(() => {});
    playSfx('complete');
    navigate('/learn/lesson-01-hello', { replace: true });
  };

  return (
    <Screen>
      <Container size="sm">
        <div className="fluent-onb">
          <div className="fluent-onb__progress" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="fluent-onb__progress-dot"
                data-active={i <= step || undefined}
              />
            ))}
          </div>

          {step === 0 && (
            <Stack gap={6} align="center">
              <div className="fluent-onb__hero" aria-hidden="true">👋</div>
              <Heading level={1} style={{ textAlign: 'center' }}>
                {t('onboarding.welcome.title')}
              </Heading>
              <Text size="lg" tone="muted" align="center">
                {t('onboarding.welcome.subtitle')}
              </Text>
              <Button variant="primary" size="lg" fullWidth onClick={() => next(1)}>
                {t('onboarding.welcome.cta')}
              </Button>
            </Stack>
          )}

          {step === 1 && (
            <Stack gap={6}>
              <Heading level={2} style={{ textAlign: 'center' }}>
                {t('onboarding.level.title')}
              </Heading>
              <Stack gap={3}>
                <ChoiceCard
                  selected={level === 'beginner'}
                  onPress={() => {
                    playSfx('tap');
                    setLevel('beginner');
                  }}
                  leading={<span className="fluent-onb__choice-icon" aria-hidden="true">🌱</span>}
                  title={t('onboarding.level.beginner_title')}
                  description={t('onboarding.level.beginner_desc')}
                />
                <ChoiceCard
                  selected={level === 'basics'}
                  onPress={() => {
                    playSfx('tap');
                    setLevel('basics');
                  }}
                  leading={<span className="fluent-onb__choice-icon" aria-hidden="true">📚</span>}
                  title={t('onboarding.level.basics_title')}
                  description={t('onboarding.level.basics_desc')}
                />
              </Stack>
              <Button
                variant="primary"
                size="lg"
                fullWidth
                disabled={!level}
                onClick={() => next(2)}
              >
                {t('onboarding.continue')}
              </Button>
            </Stack>
          )}

          {step === 2 && (
            <Stack gap={6}>
              <Heading level={2} style={{ textAlign: 'center' }}>
                {t('onboarding.goal.title')}
              </Heading>
              <Text size="base" tone="muted" align="center">
                {t('onboarding.goal.subtitle')}
              </Text>
              <Stack gap={2}>
                {GOALS.map((g) => (
                  <ChoiceCard
                    key={g.minutes}
                    selected={goal === g.minutes}
                    onPress={() => {
                      playSfx('tap');
                      setGoal(g.minutes);
                    }}
                    leading={
                      <span className="fluent-onb__choice-mins" aria-hidden="true">
                        {g.minutes}
                      </span>
                    }
                    title={t(g.titleKey)}
                    description={t(g.descKey)}
                  />
                ))}
              </Stack>
              {error && (
                <p role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--text-sm)', fontWeight: 700 }}>
                  {error}
                </p>
              )}
              <Button
                variant="primary"
                size="lg"
                fullWidth
                disabled={!goal}
                loading={submitting}
                onClick={finish}
              >
                {t('onboarding.start')}
              </Button>
            </Stack>
          )}
        </div>
      </Container>
    </Screen>
  );
}
